import React, { useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Wallet, ArrowUpCircle, ArrowDownCircle, Clock, CheckCircle, AlertCircle, Pause } from "lucide-react";

function txIcon(type) {
  const map = {
    deposit: <ArrowUpCircle size={16} className="text-emerald-500" />,
    earnings: <ArrowUpCircle size={16} className="text-emerald-500" />,
    release_hold: <ArrowUpCircle size={16} className="text-emerald-500" />,
    withdraw: <ArrowDownCircle size={16} className="text-rose-500" />,
    deduction: <ArrowDownCircle size={16} className="text-rose-500" />,
    hold: <Pause size={16} className="text-amber-500" />,
    pending_update: <Clock size={16} className="text-amber-500" />,
    admin_adjustment: <AlertCircle size={16} className="text-violet-500" />,
  };
  return map[type] || <Wallet size={16} className="text-muted-foreground" />;
}

function txLabel(type) {
  const map = {
    deposit: "Deposit",
    earnings: "Campaign Earnings",
    withdraw: "Withdrawal",
    deduction: "Deduction",
    hold: "Hold Placed",
    release_hold: "Released from Hold",
    pending_update: "Pending Update",
    admin_adjustment: "Admin Adjustment",
  };
  return map[type] || type || "Transaction";
}

function txStatusBadge(tx) {
  if (tx.status === "completed" || tx.type === "deposit" || tx.type === "release_hold") {
    return <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">Completed</span>;
  }
  if (tx.type === "hold") {
    return <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">On Hold</span>;
  }
  if (tx.type === "pending_update") {
    return <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">Pending</span>;
  }
  return null;
}

export function WalletPage({ embedded = false } = {}) {
  const { me } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [error, setError] = useState(null);
  const isDark = document.documentElement.classList.contains("dark");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/wallet");
        setWallet(res.data);
      } catch (e) {
        setError(apiErrorMessage(e));
      }
    })();
  }, []);

  const txList = useMemo(() => (wallet?.transactions || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at)), [wallet]);

  return (
    <div data-testid="wallet-page" className="space-y-6">
      {!embedded && (
        <div>
          <h1 data-testid="wallet-title" className="page-title">Wallet</h1>
          <p className="page-subtitle">Your earnings and transaction history</p>
        </div>
      )}

      {error && (
        <div data-testid="wallet-error" className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">{error}</div>
      )}

      {/* Balance Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4">
          <div className="text-xs font-medium uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Available</div>
          <div data-testid="wallet-available" className="mt-2 text-3xl font-black tabular-nums text-[#c8ff00]" style={{ textShadow: "0 0 18px rgba(200,255,0,0.2)" }}>
            ${Number(wallet?.available_balance || 0).toFixed(2)}
          </div>
        </div>
        <div className="rounded-2xl border border-amber-500/20 bg-amber-500/[0.04] p-4">
          <div className="text-xs font-medium uppercase tracking-wider text-amber-700 dark:text-amber-400">Pending</div>
          <div data-testid="wallet-pending" className="mt-2 text-3xl font-black tabular-nums text-amber-700 dark:text-amber-400">
            ${Number(wallet?.pending_balance || 0).toFixed(2)}
          </div>
        </div>
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/[0.04] p-4">
          <div className="text-xs font-medium uppercase tracking-wider text-rose-700 dark:text-rose-400">On Hold</div>
          <div data-testid="wallet-on-hold" className="mt-2 text-3xl font-black tabular-nums text-rose-700 dark:text-rose-400">
            ${Number(wallet?.on_hold_balance || 0).toFixed(2)}
          </div>
        </div>
        <div className="rounded-2xl border border-border/60 bg-card/70 p-4">
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">All-Time</div>
          <div data-testid="wallet-all-time" className="mt-2 text-3xl font-black tabular-nums text-foreground">
            ${Number(wallet?.total_earned || 0).toFixed(2)}
          </div>
        </div>
      </div>

      {/* Transactions */}
      <Card data-testid="wallet-transactions" className="border-border/60 bg-card/70">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Transaction History</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {txList.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border/60 px-4 py-8 text-center text-sm text-muted-foreground">
              No transactions yet
            </div>
          ) : txList.map((tx, i) => (
            <div
              key={tx.id || i}
              data-testid={`tx-${tx.id || i}`}
              className="rounded-xl border border-border/60 bg-background px-4 py-3"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 shrink-0">{txIcon(tx.type)}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`text-base font-bold tabular-nums ${(tx.amount || 0) >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
                        {(tx.amount || 0) >= 0 ? "+" : ""}{`$${Math.abs(tx.amount || 0).toFixed(2)}`}
                      </span>
                      <span className="text-xs font-medium text-muted-foreground">{txLabel(tx.type)}</span>
                    </div>
                    {txStatusBadge(tx)}
                  </div>

                  {(tx.note || tx.description) && (
                    <div className="mt-1 text-xs text-muted-foreground">{tx.note || tx.description}</div>
                  )}

                  {tx.admin_note && (
                    <div className="mt-1 text-xs italic text-violet-600 dark:text-violet-400">"{tx.admin_note}"</div>
                  )}

                  <div className="mt-1.5 flex items-center gap-2 text-[10px] text-muted-foreground/60">
                    {tx.created_at && <span>{new Date(tx.created_at).toLocaleString()}</span>}
                    {tx.tag && (
                      <Badge className="bg-muted text-muted-foreground text-[9px] px-1.5 py-0 hover:bg-muted">{tx.tag}</Badge>
                    )}
                    {tx.actor && <span>by {tx.actor}</span>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
